import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import type { IconStyle } from '@builder4/shared/types/icon';
import { formatIconName } from '@builder4/shared/utils/iconHelpers';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import IconClose from '@/assets/icons/icon-close.svg?react';
import { IconPicker } from '@/components/inspector/InspectorIcon/IconPicker';
import { getIconManager } from '@builder4/shared/icons/manager';

interface IconFieldProps {
  className?: string;
  text?: string;
  iconName: string;
  iconStyle: IconStyle;
  onSelect: (name: string, style: IconStyle) => void;
  onClear?: () => void;
  /** WHY: Ref to a container outside ScrollArea so the picker isn't clipped by overflow */
  pickerContainerRef?: React.RefObject<HTMLElement | null>;
}

export const IconField: React.FC<IconFieldProps> = ({
  className,
  text = 'Select Icon',
  iconName,
  iconStyle,
  onSelect,
  onClear,
  pickerContainerRef,
}) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const iconManager = getIconManager();
  const hasIcon = Boolean(iconName && iconManager?.hasIcon(iconName));

  const picker = (
    <IconPicker
      isOpen={isPickerOpen}
      onClose={() => setIsPickerOpen(false)}
      onSelect={onSelect}
      selectedIcon={iconName}
      selectedStyle={iconStyle}
    />
  );

  return (
    <>
      <div className={clsx('flex items-center justify-between', className)}>
        <span className="text-button-sm text-neutral-500">{text}</span>
        <div
          className="relative cursor-pointer"
          role="button"
          aria-label="Select icon"
          tabIndex={0}
          onClick={() => setIsPickerOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setIsPickerOpen(true);
          }}
        >
          {hasIcon && (
            <div className="absolute top-[7px] left-1.5 z-10 flex h-4.5 w-4.5 items-center justify-center">
              <i className={`fa-${iconStyle} fa-${iconName} text-sm text-neutral-700`} />
            </div>
          )}
          <input
            readOnly
            className={clsx(
              'text-button-sm flex h-8 w-full max-w-[112px] min-w-0 cursor-pointer rounded-md bg-neutral-50 p-1.5 !text-neutral-900 placeholder-neutral-500 outline outline-offset-[-1px] outline-transparent transition-all placeholder:text-xs placeholder:font-medium',
              hasIcon && 'pr-7 pl-8',
            )}
            value={hasIcon ? formatIconName(iconName) : ''}
            placeholder="Select"
          />
          {hasIcon && onClear && (
            <IconSimpleButton
              icon={IconClose}
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              aria-label="Clear icon"
              className="absolute top-1/2 right-1 -translate-y-1/2"
            />
          )}
        </div>
      </div>

      {pickerContainerRef?.current ? createPortal(picker, pickerContainerRef.current) : picker}
    </>
  );
};
